<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, inject } from 'vue';
import { EventBus, useDialogPluginComponent } from 'quasar';

import { breakpoints } from '@/composables/useBreakpoints.ts';
import { copyToClipboardWithNotification } from '@/helpers/clipboardUtils.ts';
import { useHotKeys } from '@/composables/hotKeys/useHotkeys.ts';
import { HotKeyActionId } from '@/composables/hotKeys/types';
import { FILE_DIALOG_HOT_KEYS } from '@/composables/hotKeys/pageHotKeys';
import {
  EventBusEvents,
} from '@/helpers/types.ts';

import {
  ContractsFileType,
} from '@/generated/graphql.ts';

import FileViewer from '@/components/FileViewer.vue';
import { useFilesActions } from '@/composables/useFilesActions.ts';

defineEmits([...useDialogPluginComponent.emits]);
const props = defineProps<{
  fileId: string;
  fileName: string;
  fileIndex: number;
  filesArray: Array<ContractsFileType>;
}>();

const bus = inject<EventBus>('bus');
const { dialogRef: ViewContractFileDialogRef, onDialogHide } = useDialogPluginComponent();
const { openRenameDialog, download } = useFilesActions();
const localFileId = ref('');
const localFileName = ref('');
const currentIndex = ref(0);
const localFilesArray = ref<Array<ContractsFileType>>([]);

const isSmallScreen = breakpoints.smaller('small');

// EDIT NAME
const isLeastOneFileChanged = ref(false);

const isDialogFocused = ref(true);

const setFocused = (value: boolean) => {
  isDialogFocused.value = value;
};

const openEditModal = () => {
  setFocused(false);
  openRenameDialog(localFileName.value, localFileId.value, () => {
    setFocused(true);
  }).then((payload) => {
    isLeastOneFileChanged.value = true;
    localFileName.value = payload.editableValue;
    updateFileNameInArray();
  });
};

// CHANGE DOCUMENT

const changeDocument = (direction: 'next' | 'prev') => {
  const indexChange = direction === 'next' ? 1 : -1;
  const newIndex = currentIndex.value + indexChange;
  if (newIndex >= 0 && newIndex < localFilesArray.value?.length) {
    const file = localFilesArray.value[newIndex];
    localFileName.value = file.name;
    currentIndex.value = newIndex;
    localFileId.value = file.id;
  }
};

const downloadFile = () => {
  download(localFileId.value, localFileName.value);
};

const initializeLocalState = () => {
  if (props.fileId) {
    localFileId.value = props.fileId;
  }
  if (props.fileName) {
    localFileName.value = props.fileName ?? '';
  }
  if (props.fileIndex) {
    currentIndex.value = props.fileIndex;
  }
  if (Array.isArray(props.filesArray)) {
    localFilesArray.value = props.filesArray;
  }
};

const resetLocalState = () => {
  localFileId.value = '';
  localFileName.value = '';
  currentIndex.value = 0;
  localFilesArray.value = [];
  isLeastOneFileChanged.value = false;
};

const callBusEventAfterChanges = () => {
  if (isLeastOneFileChanged.value) {
    if (!bus) return;
    bus.emit(EventBusEvents.changedContractFiles);
  }
};

const updateFileNameInArray = () => {
  const file = localFilesArray.value[currentIndex.value];
  if (file.id === localFileId.value) {
    file.name = localFileName.value;
  }
};

const getHotKeyLabel = (id: string) => {
  const hotKey = FILE_DIALOG_HOT_KEYS.find(hotKey => hotKey.id === id);
  return hotKey ? hotKey.label : '';
};

const registerHotKeys = () => {
  hotKeys.registerHandler({
    actionId: HotKeyActionId.Rename,
    handler: () => {
      if (isDialogFocused.value) {
        openEditModal();
      }
    },
  });

  hotKeys.registerHandler({
    actionId: HotKeyActionId.ArrowLeft,
    handler: () => {
      if (isDialogFocused.value) {
        changeDocument('prev');
      }
    },
  });

  hotKeys.registerHandler({
    actionId: HotKeyActionId.ArrowRight,
    handler: () => {
      if (isDialogFocused.value) {
        changeDocument('next');
      }
    },
  });
};

// HOT KEYS
const hotKeys = useHotKeys();

onMounted(() => {
  initializeLocalState();
  registerHotKeys();
  hotKeys.setupHotKeys();
});

onBeforeUnmount(() => {
  hotKeys.cleanupHotKeys();
  callBusEventAfterChanges();
  resetLocalState();
});
</script>

<template>
  <q-dialog
    ref="ViewContractFileDialogRef"
    full-height
    class="view-file-dialog-wrapper contract-file-dialog"
    @show="() => setFocused(true)"
    @hide="() => setFocused(false)"
    @shake="() => setFocused(true)"
  >
    <div class="view-file-dialog row items-center justify-center no-wrap">
      <q-btn
        v-if="!isSmallScreen"
        round
        icon="arrow_left"
        color="teal-1"
        text-color="blue-grey-10"
        class="view-file-dialog__doc-btn q-mr-md"
        :disable="currentIndex === 0"
        @click.stop="changeDocument('prev')"
      />
      <q-card class="view-file-dialog__content">
        <q-card-section
          :class="{ column: isSmallScreen, reverse: isSmallScreen }"
        >
          <div class="row items-center no-wrap full-width view-file-section-header">
            <div class="col row items-center q-gutter-xs file-name-container">
              <div
                class="text-left overflow-hidden ellipsis text-subtitle2"
                style="max-width: 500px;"
                @click="openEditModal"
              >
                {{ localFileName }}
                <q-tooltip
                  class="text-body2"
                >
                  {{ localFileName }}
                </q-tooltip>
              </div>
              <q-btn
                round
                flat
                size="sm"
                icon="content_copy"
                color="primary"
                @click="copyToClipboardWithNotification(localFileName)"
              />
            </div>
            <div
              class="row q-gutter-sm no-wrap"
            >
              <div class="flex flex-center justify-center q-gutter-xs">
                <q-btn
                  flat
                  round
                  dense
                  size="md"
                  color="primary"
                  icon="download"
                  @click="downloadFile"
                >
                  <q-tooltip
                    class="text-body2"
                  >
                    Download
                  </q-tooltip>
                </q-btn>
                <q-btn
                  round
                  flat
                  size="md"
                  icon="drive_file_rename_outline"
                  color="primary"
                  @click="openEditModal"
                >
                  <q-tooltip
                    class="text-body2"
                  >
                    Rename {{ getHotKeyLabel(HotKeyActionId.Rename) }}
                  </q-tooltip>
                </q-btn>
                <q-btn
                  flat
                  round
                  dense
                  size="md"
                  color="primary"
                  icon="close"
                  class="q-ml-md"
                  @click="onDialogHide"
                />
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none view-file-dialog__section_body">
          <FileViewer
            :file-id="localFileId"
            :file-name="localFileName"
          />
        </q-card-section>
      </q-card>
      <q-btn
        v-if="!isSmallScreen"
        round
        icon="arrow_right"
        color="teal-1"
        text-color="blue-grey-10"
        class="view-file-dialog__doc-btn q-ml-md"
        :disable="currentIndex === localFilesArray?.length - 1"
        @click.stop="changeDocument('next')"
      />
    </div>
  </q-dialog>
</template>

<style lang="scss">
.view-file-dialog-wrapper {
  overflow: hidden;
  width: 100%;
  height: 100%;
}

/*
  Классы .view-file-dialog* используются несколькими вьюерами файлов и объявлены
  у них в глобальных style-блоках, поэтому правила из соседних компонентов
  (например position: sticky для стрелок) протекают сюда и ломают вёрстку.
  Вкладываем свои правила в .contract-file-dialog, чтобы специфичность была выше
  и раскладка не зависела от порядка загрузки чанков.
*/
.contract-file-dialog {
  .view-file-dialog {
    width: 80%;
    max-width: 80% !important;
    height: 100%;
    flex-wrap: nowrap;

    @media screen and (max-width: 780px) {
      width: 100%;
      max-width: 100% !important;
    }
  }

  /*
    relative, а не static: на position у .q-btn держится дочерний .q-focus-helper
    (absolute, 100% x 100%, border-radius: inherit). При static он растягивается
    по карточке и превращается в огромный овал с ховером. relative гасит
    протёкший sticky и сохраняет кнопке свой containing block.
  */
  .view-file-dialog__doc-btn {
    position: relative;
    top: auto;
    transform: none;
    flex: 0 0 auto;
  }

  .view-file-dialog__content {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .view-file-dialog__section_body {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
    max-height: none;
    overflow: auto;
  }
}

.input-editable {
  width: 200px;
}

.justify-self-end {
  justify-self: end;
}
</style>

<style scoped lang="scss">
.view-file-section-header {
  height: 44px;
}

.file-name-container {
  width: 300px;
}
</style>
